import React, { useState, useEffect } from 'react'
import { Card, Typography, Table, Form, Input, Button, message, Space } from 'antd'
import api from '../../utils/api'
import { formatTz } from '../../utils/timezone'

const { Title, Paragraph } = Typography

const PlatformAdminsPage = () => {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [data, setData] = useState([])
  const [form] = Form.useForm()

  const fetchAdmins = async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/admin/platform-admins')
      if (res.data?.success) {
        setData(res.data.data || [])
      } else {
        message.error(res.data?.message || '載入失敗')
      }
    } catch (e) {
      message.error(e.response?.data?.message || '載入失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAdmins()
  }, [])

  const onCreate = async (values) => {
    setSubmitting(true)
    try {
      const res = await api.post('/api/admin/platform-admins', {
        username: String(values.username || '').trim(),
        password: values.password,
        name: values.name ? String(values.name).trim() : undefined
      })
      if (res.data?.success) {
        message.success('已建立管理員帳號')
        form.resetFields()
        fetchAdmins()
      } else {
        message.error(res.data?.message || '建立失敗')
      }
    } catch (e) {
      message.error(e.response?.data?.message || '建立失敗')
    } finally {
      setSubmitting(false)
    }
  }

  const columns = [
    { title: '帳號', dataIndex: 'username', key: 'username' },
    {
      title: '權限',
      dataIndex: 'platformAdminTier',
      key: 'platformAdminTier',
      render: (t) => (t === 'staff' ? '子管理員' : '超級管理員')
    },
    { title: '顯示名稱', dataIndex: 'name', key: 'name', render: (v) => v || '—' },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => formatTz(d, 'YYYY/MM/DD HH:mm', '—')
    }
  ]

  return (
    <Card>
      <Title level={2}>後台管理員帳號</Title>
      <Paragraph type='secondary' style={{ marginBottom: 24 }}>
        僅<strong>超級管理員</strong>可進入此前台與使用本頁。此處新增的帳號為
        <strong>子管理員</strong>：可登入「管理員登入」以協助{' '}
        <strong>/register</strong> 商家註冊，但<strong>無法</strong>進入{' '}
        <code>/admin</code> 管理後台。新帳號建立後請自行將帳密交給同仁；系統不會再次顯示密碼。
      </Paragraph>

      <Card type='inner' title='新增管理員' style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout='vertical'
          onFinish={onCreate}
          style={{ maxWidth: 420 }}
        >
          <Form.Item
            label='登入帳號'
            name='username'
            rules={[{ required: true, message: '請輸入帳號' }]}
          >
            <Input autoComplete='off' placeholder='英數組合，勿與現有帳號重複' />
          </Form.Item>
          <Form.Item
            label='顯示名稱（選填）'
            name='name'
          >
            <Input placeholder='例如：營運－王小明' />
          </Form.Item>
          <Form.Item
            label='密碼'
            name='password'
            rules={[
              { required: true, message: '請輸入密碼' },
              { min: 8, message: '至少 8 個字元' }
            ]}
          >
            <Input.Password autoComplete='new-password' placeholder='至少 8 個字元' />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type='primary' htmlType='submit' loading={submitting}>
                建立帳號
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Title level={4}>現有管理員</Title>
      <Table
        rowKey='_id'
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
      />
    </Card>
  )
}

export default PlatformAdminsPage
